/**
 * The power-ups that clear the screen (spec 02 §7.5): the blue cross's flood (level-transition.s
 * D_2DD7), the purple lamp's six flying pieces (D_2ECC, super-bonus.s D_2A18) and the bomb's pulsing
 * blast (bonus-stage-extended.s D_35B0, D_2A74). Every monster dies (D_2E61) and the level then clears
 * as usual - the C64 zeroes its monster count ($4A), so the ordinary clear follows, with its bonus.
 */

import { announce, clearSoftware, killAll, pause } from './act';
import { emptySlot } from './slots';
import {
  BLAST_FLASH_FRAMES, BOMB_BIG_X, BOMB_BIG_Y, BOMB_FRAMES, BOMB_SMALL_X, BOMB_SMALL_Y, BOMB_TOGGLE_FRAMES,
  FLOOD_FRAMES_PER_ROW, FLOOD_ROWS, LAMP_END_Y, LAMP_VX, LAMP_VY, LAMP_X, LAMP_Y,
} from './tuning';
import type { ItemSystem } from './state';
import type { PlayerId, World } from '../types';

/** Frames in an I-tick: the flood and the blasts are timed in frames, stepped on I-ticks. */
const I_TICK_FRAMES = 2;

/**
 * D_2DD7: the software sprites cleared, both item slots emptied ($2DF8: $52 = $53 = $FF), then the
 * water rises from the bottom row, one row every 2 frames; the main loop waits meanwhile, the players
 * and monsters (IRQ) do not. At the top every monster dies.
 */
export function takeBlueCross(world: World, sys: ItemSystem, by: PlayerId): void {
  clearSoftware(world);
  emptySlot(world, sys.food);
  emptySlot(world, sys.powerup);
  sys.flood = 0;
  sys.floodFrames = 0;
  sys.floodBy = by;
  announce(world, 'flood');
}

/** One I-tick of the flood. The water stays drawn for the rest of the level (its characters stay). */
export function floodTick(world: World, sys: ItemSystem): void {
  if (sys.floodFrames < 0) return;
  sys.floodFrames += I_TICK_FRAMES;
  sys.flood = Math.min(FLOOD_ROWS, Math.floor(sys.floodFrames / FLOOD_FRAMES_PER_ROW));
  if (sys.flood < FLOOD_ROWS) return;
  sys.floodFrames = -1;
  killAll(world, sys.floodBy);
}

/**
 * D_2ECC (lamp) / D_35B0 (bomb): the software sprites cleared, every monster killed at once (D_2E61),
 * then the border flashes 6 x 3 frames with play paused (D_2EEA) and the six monster sprites become
 * the blast. (On the C64 the dying monsters are hidden under it and carry on afterwards, $2A3F.)
 */
export function takeBlast(world: World, sys: ItemSystem, kind: 'lamp' | 'bomb', by: PlayerId): void {
  clearSoftware(world);
  killAll(world, by);
  sys.blast = kind;
  sys.blastTicks = 0;
  pause(world, sys, BLAST_FLASH_FRAMES);
  announce(world, 'explosion');
}

/** One I-tick of the blast: the lamp's pieces fly until the first is above Y $50; the bomb lasts ~64 frames. */
export function blastTick(sys: ItemSystem): void {
  if (sys.blast === 'none') return;
  sys.blastTicks++;
  const over = sys.blast === 'lamp'
    ? LAMP_Y + (LAMP_VY[0] ?? 0) * sys.blastTicks < LAMP_END_Y
    : sys.blastTicks * I_TICK_FRAMES >= BOMB_FRAMES;
  if (over) sys.blast = 'none';
}

/** A blast sprite: VIC position (as a monster's), and whether it is drawn double size. */
export interface BlastPiece {
  x: number;
  y: number;
  big: boolean;
}

/** Where the blast's six sprites are now. */
export function blastPieces(sys: ItemSystem): BlastPiece[] {
  const t = sys.blastTicks;
  if (sys.blast === 'lamp') {
    return LAMP_VX.map((vx, i) => ({ x: LAMP_X + vx * t, y: LAMP_Y + (LAMP_VY[i] ?? 0) * t, big: false }));
  }
  if (sys.blast !== 'bomb') return [];
  // It starts on the plain pattern (L_2AAD) and toggles every 4 frames.
  const big = Math.floor((t * I_TICK_FRAMES) / BOMB_TOGGLE_FRAMES) % 2 === 1;
  const xs = big ? BOMB_BIG_X : BOMB_SMALL_X;
  const ys = big ? BOMB_BIG_Y : BOMB_SMALL_Y;
  return xs.map((x, i) => ({ x, y: ys[i] ?? 0, big }));
}

/** 0..1 through the blast's life. */
export function blastProgress(sys: ItemSystem): number {
  if (sys.blast === 'lamp') return Math.min(1, sys.blastTicks / ((LAMP_Y - LAMP_END_Y) / 4 + 1));
  if (sys.blast === 'bomb') return Math.min(1, (sys.blastTicks * I_TICK_FRAMES) / BOMB_FRAMES);
  return 1;
}
